import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { X, Copy, Check, Gift } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Stores a single 'redeemed' boolean so the popup stops appearing after the
// visitor has used the code at checkout. Non-sensitive UI state — localStorage
// is the correct tier. See /app/CODE_REVIEW_NOTES.md §4.
const REDEEMED_KEY = 'ww-builder-discount-redeemed';

/**
 * Popup that appears once per visit (per page-mount) after the AI photo has rendered.
 * Stops re-appearing forever (across visits) only once the visitor has *redeemed* the code at checkout.
 * Admin configures code/percent/copy via /admin/discounts.
 *
 * Props:
 *   open: boolean — caller controls when to show (e.g. after AI image loads)
 *   onClose: () => void
 */
export default function BuilderDiscountPopup({ open, onClose }) {
  const [discount, setDiscount] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const fetchedRef = useRef(false);

  // Fetch the current discount once when caller first wants to show
  useEffect(() => {
    if (!open || fetchedRef.current) return;
    fetchedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(`${API}/discounts/builder`);
        if (!cancelled) setDiscount(data);
      } catch (err) {
        if (!cancelled) setDiscount(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // If admin disabled it, or visitor already redeemed → close immediately
  useEffect(() => {
    if (!open) return;
    if (typeof window !== 'undefined' && window.localStorage.getItem(REDEEMED_KEY)) {
      onClose?.();
      return;
    }
    if (discount && discount.enabled === false) {
      onClose?.();
    }
  }, [open, discount, onClose]);

  if (!open) return null;
  if (loading || !discount || discount.enabled === false) return null;

  const code = (discount.code || '').toUpperCase();
  const percent = discount.percent_off || 0;
  const headline = discount.headline || 'A little gift for designing yours';
  const subhead = (discount.subhead || '').replace(/\{percent\}/g, `${percent}`);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success(`Code ${code} copied!`);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Couldn't copy — please copy the code manually");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-[#354F52]/60 backdrop-blur-sm"
      data-testid="builder-discount-popup"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative w-full max-w-md bg-[#FAF9F6] rounded-sm shadow-2xl overflow-hidden">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close discount offer"
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-[#52796F] hover:text-[#354F52] hover:bg-[#354F52]/5 transition-colors z-10"
          data-testid="builder-discount-close"
        >
          <X className="w-5 h-5" strokeWidth={1.5} />
        </button>

        <div className="p-8 md:p-10 text-center">
          <div className="w-16 h-16 rounded-full bg-[#D4AF37]/15 flex items-center justify-center mx-auto mb-5">
            <Gift className="w-8 h-8 text-[#D4AF37]" strokeWidth={1.5} />
          </div>

          <p className="text-caption text-[#84A98C] mb-3">{percent}% OFF · YOUR DESIGN</p>
          <h2 className="font-serif text-2xl md:text-3xl text-[#354F52] mb-3 leading-tight" data-testid="builder-discount-headline">
            {headline}
          </h2>
          <p className="text-[#52796F] text-sm mb-7" data-testid="builder-discount-subhead">
            {subhead}
          </p>

          <div className="bg-[#F5F5F0] border border-dashed border-[#84A98C] rounded-sm p-5 mb-6">
            <p className="text-caption text-[#84A98C] mb-2">Your code</p>
            <p
              className="font-serif text-3xl md:text-4xl text-[#354F52] tracking-widest mb-4 select-all"
              data-testid="builder-discount-code"
            >
              {code}
            </p>
            <Button
              type="button"
              onClick={handleCopy}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="builder-discount-copy"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4" strokeWidth={1.5} />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" strokeWidth={1.5} />
                  Copy code
                </>
              )}
            </Button>
          </div>

          <p className="text-xs text-[#84A98C]">
            Apply <span className="font-medium text-[#354F52]">{code}</span> at checkout. One use per customer.
          </p>
        </div>
      </div>
    </div>
  );
}
